<template>
  <div
    v-if="show"
    class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
    @click.self="close"
  >
    <div class="bg-white rounded-2xl shadow-2xl w-[90%] max-w-lg p-6">
      <h3 class="text-lg font-bold text-gray-800 mb-1">Вернуть изделие на этап</h3>
      <p class="text-sm text-gray-500 mb-4">Изделие {{ productLabel }}</p>

      <!-- Загрузка операций маршрута -->
      <div v-if="loading" class="text-sm text-gray-500 py-4">Загружаем операции маршрута…</div>

      <template v-else-if="operations.length">
        <label class="block text-sm font-medium text-gray-700 mb-1">
          Операция, с которой изделие проходится заново
          <span class="text-red-600">*</span>
        </label>
        <div class="v-select-filter return-to-stage-select" style="width: 100%; flex: none;">
          <v-select
            v-model="selectedOperationId"
            :options="operations"
            :reduce="operation => operation.id"
            :get-option-label="operationLabel"
            :clearable="false"
            :disabled="submitting"
            placeholder="Выберите операцию"
          >
            <!-- Длинное название операции обрезается многоточием, полный текст — в подсказке:
                 иначе подпись выходит за рамку поля и налезает на заголовок над ним -->
            <template #selected-option="operation">
              <span class="block truncate max-w-full" :title="operationLabel(operation)">
                {{ operationLabel(operation) }}
              </span>
            </template>
            <template #option="operation">
              <div class="py-0.5">
                <div class="font-medium text-gray-800">{{ operationName(operation) }}</div>
                <div class="text-xs text-gray-500">
                  {{ operation.workshop_post_name || 'Рабочий центр не указан' }} ·
                  {{ operation.status_label }}
                  <span v-if="operation.attempts_count > 0"> · {{ attemptsLabel(operation.attempts_count) }}</span>
                </div>
              </div>
            </template>
          </v-select>
        </div>

        <p v-if="selectedOperationAttempts > 0" class="text-xs text-amber-700 mt-2">
          Эту операцию уже проводили: {{ attemptsLabel(selectedOperationAttempts) }}. Прошлые попытки сохранятся в истории.
        </p>

        <label class="block text-sm font-medium text-gray-700 mt-4 mb-1">
          В чём была проблема
          <span class="text-red-600">*</span>
        </label>
        <textarea
          v-model="comment"
          :disabled="submitting"
          rows="4"
          :maxlength="commentMaxLength"
          placeholder="Что именно не так с изделием — этот текст попадёт в реестр несоответствий"
          class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 resize-y"
        ></textarea>
        <p class="text-xs text-gray-500 mt-1">{{ comment.length }} из {{ commentMaxLength }} символов</p>

        <p class="text-xs text-gray-500 mt-2">
          Выбранная операция и следующие за ней проводятся заново, предыдущие сохраняют зачёт.
          Заполненный акт о выпуске сохраняется, но выпустить изделие можно будет только
          после повторного завершения задания.
        </p>

        <p v-if="error" class="text-sm text-red-600 mt-3 whitespace-pre-line break-words">{{ error }}</p>

        <div class="flex justify-end gap-2 mt-5">
          <button
            type="button"
            class="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 disabled:opacity-50"
            :disabled="submitting"
            @click="close"
          >
            Отмена
          </button>
          <button
            type="button"
            class="px-4 py-2 bg-amber-600 text-white rounded-lg font-medium hover:bg-amber-700 disabled:opacity-50"
            :disabled="submitting"
            @click="submit"
          >
            {{ submitting ? 'Возвращаем…' : 'Вернуть на этап' }}
          </button>
        </div>
      </template>

      <!-- Операции получить не удалось либо маршрут пуст -->
      <template v-else>
        <p class="text-sm text-red-600 mb-5 whitespace-pre-line break-words">
          {{ error || 'У задания нет операций маршрута — возвращать изделие не на что.' }}
        </p>
        <div class="flex justify-end">
          <button
            type="button"
            class="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300"
            @click="close"
          >
            Закрыть
          </button>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

/**
 * Возврат изделия на этап маршрута со стола «Выпуск продукта» без остановки (Э08).
 *
 * Отличие от «Вернуть на маршрут» (RouteReturnModal): там возврат открывает согласованный
 * документ качества по остановленному изделию, здесь специалист качества сам, не выпуская
 * и не бракуя, отправляет завершённое задание на операцию с обязательным комментарием —
 * комментарий становится описанием случая несоответствия.
 *
 * Список операций берём у существующего getReturnOptions: признаки is_available и reason
 * этого сценария не касаются (они про возврат остановленного изделия) и игнорируются.
 */
export default {
  name: 'ReturnToStageModal',
  props: {
    show: { type: Boolean, default: false },
    // Задание стола выпуска — им адресуется возврат
    productionTaskId: { type: [Number, String], default: null },
    // Изделие задания — по нему запрашиваются операции маршрута
    batchProductId: { type: [Number, String], default: null },
    serialNumber: { type: String, default: '' },
  },
  emits: ['close', 'returned', 'failed'],
  data() {
    return {
      // Предел тот же, что в ReturnToStageRequest
      commentMaxLength: 2000,
      loading: false,
      submitting: false,
      operationsRaw: [],
      selectedOperationId: null,
      comment: '',
      error: '',
    };
  },
  computed: {
    productLabel() {
      return this.serialNumber ? `№ ${this.serialNumber}` : 'без серийного номера';
    },
    // Порядок операций по маршруту: показываем так, как их проходит изделие
    operations() {
      return [...this.operationsRaw].sort((a, b) => (a.sort || 0) - (b.sort || 0));
    },
    selectedOperation() {
      return this.operations.find(operation => operation.id === this.selectedOperationId) || null;
    },
    selectedOperationAttempts() {
      return this.selectedOperation?.attempts_count || 0;
    },
  },
  watch: {
    show(value) {
      if (value) {
        this.init();
      } else {
        this.reset();
      }
    },
  },
  mounted() {
    if (this.show) this.init();
  },
  methods: {
    init() {
      this.reset();
      this.loadOperations();
    },
    reset() {
      this.operationsRaw = [];
      this.selectedOperationId = null;
      this.comment = '';
      this.error = '';
      this.loading = false;
      this.submitting = false;
    },
    async loadOperations() {
      const batchProductId = Number(this.batchProductId) || null;
      if (!batchProductId) {
        this.error = 'Не указано, по какому изделию нужны операции маршрута.';
        return;
      }

      this.loading = true;
      this.error = '';
      try {
        const { data } = await axios.post('/api/nonconformity/getReturnOptions', {
          batch_product_id: batchProductId,
        });
        if (data?.success) {
          // is_available и reason относятся к возврату остановленного изделия — здесь не смотрим
          this.operationsRaw = data.data?.operations || [];
          return;
        }
        this.error = data?.message || 'Не удалось получить операции маршрута.';
      } catch (error) {
        this.error = this.extractError(error, 'Не удалось получить операции маршрута.');
      } finally {
        this.loading = false;
      }
    },
    async submit() {
      if (this.submitting) return;

      // Те же правила, что в ReturnToStageRequest
      const comment = (this.comment || '').trim();
      if (!this.selectedOperationId) {
        this.error = 'Выберите операцию, с которой изделие проходится заново.';
        return;
      }
      if (!comment) {
        this.error = 'Укажите, в чём была проблема: комментарий обязателен.';
        return;
      }
      if (comment.length > this.commentMaxLength) {
        this.error = `Комментарий длиннее ${this.commentMaxLength} символов.`;
        return;
      }

      const productionTaskId = Number(this.productionTaskId) || null;
      if (!productionTaskId) {
        this.error = 'Не указано, какое задание возвращается на этап.';
        return;
      }

      this.submitting = true;
      this.error = '';
      try {
        const { data } = await axios.post('/api/smk/returnToStage', {
          production_task_id: productionTaskId,
          operation_id: this.selectedOperationId,
          comment,
        });
        if (data?.success) {
          this.$emit('returned', data.message || 'Изделие возвращено на этап.');
          return;
        }
        this.fail(data?.message || 'Не удалось вернуть изделие на этап.');
      } catch (error) {
        this.fail(this.extractError(error, 'Не удалось вернуть изделие на этап.'));
      } finally {
        this.submitting = false;
      }
    },
    // Отказ показываем и в модалке, и тостом: модалка остаётся открытой для правки
    fail(message) {
      this.error = message;
      this.$emit('failed', message);
    },
    close() {
      if (this.submitting) return;
      this.$emit('close');
    },
    operationName(operation) {
      return operation?.name || 'Операция без названия';
    },
    // Подпись в закрытом селекте: сырые ключи статуса на экран не выводим
    operationLabel(operation) {
      const parts = [this.operationName(operation)];
      if (operation?.workshop_post_name) parts.push(operation.workshop_post_name);
      if (operation?.status_label) parts.push(operation.status_label);
      return parts.join(' · ');
    },
    attemptsLabel(count) {
      const number = Number(count) || 0;
      const lastTwo = number % 100;
      const last = number % 10;
      let word = 'раз';
      if (last >= 2 && last <= 4 && (lastTwo < 12 || lastTwo > 14)) word = 'раза';
      return `${number} ${word}`;
    },
    // Запрет возврата приходит 422 с готовым текстом — показываем его пользователю
    extractError(error, fallback) {
      const payload = error?.response?.data;
      if (payload?.message) return payload.message;
      const details = payload?.errors && typeof payload.errors === 'object'
        ? Object.values(payload.errors).flat().filter(Boolean)
        : [];
      return details.length ? details.join('\n') : fallback;
    },
  },
};
</script>

<style scoped>
/* Выбранная операция занимает строку поля целиком и обрезается, а не переносится */
.return-to-stage-select :deep(.vs__selected-options) {
  min-width: 0;
  overflow: hidden;
}

.return-to-stage-select :deep(.vs__selected) {
  max-width: 100%;
  overflow: hidden;
}
</style>
